import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Copy, Home, RotateCcw, Send, Wallet } from 'lucide-react';
import { api, type SessionGetResponse, type SessionRewriteResponse } from '../../lib/api';
import { haptic } from '../../lib/tma';
import { cn } from '../../lib/cn';
import { notifyTurboCap } from '../../lib/turboNotice';
import { paidRewriteLabel, usePricing } from '../../lib/pricing';
import { RewriteModal } from '../RewriteModal';
import {
  clearFeedback as clearRewriteFeedback,
  loadFeedback as loadRewriteFeedback,
  saveFeedback as saveRewriteFeedback,
} from '../../lib/rewriteFeedbackStorage';
import { Button, Card, Spinner, Toast } from '../ui';
import { SlideCarousel } from './SlideCarousel';

export interface CarouselResultViewProps {
  sessionId: number;
  rewriteCost: 'free' | 'paid' | 'blocked';
  initialOpenRewrite: boolean;
  readonly?: boolean;
  onEditAnswers: () => void;
}

export function CarouselResultView({
  sessionId,
  rewriteCost,
  initialOpenRewrite,
  readonly = false,
  onEditAnswers,
}: CarouselResultViewProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [rewriteOpen, setRewriteOpen] = useState(initialOpenRewrite);
  const [copied, setCopied] = useState<'text' | 'caption' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'destructive'; text: string } | null>(
    null,
  );

  useEffect(() => {
    if (initialOpenRewrite) setRewriteOpen(true);
  }, [initialOpenRewrite]);

  const sessionData = qc.getQueryData<SessionGetResponse>(['session', sessionId]);
  const generation = sessionData?.last_generation ?? null;
  const render = sessionData?.render ?? null;
  const styleCode = sessionData?.style_code ?? null;

  const stylesQuery = useQuery({
    queryKey: ['carousel-styles'],
    queryFn: () => api.listCarouselStyles(),
    staleTime: 5 * 60_000,
  });
  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
    refetchOnWindowFocus: false,
  });
  const balance = profileQuery.data?.user.balanceCredits ?? null;
  const isAdmin = profileQuery.data?.user.isAdmin ?? false;
  const pricing = usePricing();
  const turboUntil = profileQuery.data?.user.turboUntil ?? null;

  const rerender = useMutation({
    mutationFn: (code: string) => api.renderSession(sessionId, code),
    onSuccess: ({ render: r }) => {
      haptic('success');
      qc.setQueryData<SessionGetResponse>(['session', sessionId], (old) =>
        old ? { ...old, render: r, style_code: r.style_code } : old,
      );
    },
    onError: (e) => {
      haptic('error');
      setNotice({
        tone: 'destructive',
        text: `Не удалось перерисовать: ${e instanceof Error ? e.message : ''}`,
      });
    },
  });

  const deliver = useMutation({
    mutationFn: () => api.deliverSession(sessionId),
    onSuccess: (r) => {
      haptic(r.ok ? 'success' : 'warning');
      setNotice(
        r.ok
          ? { tone: 'success', text: 'Отправили слайды в Telegram' }
          : {
              tone: 'destructive',
              text: 'Бот не смог отправить файлы. Откройте бота и нажмите /start.',
            },
      );
    },
    onError: (e) => {
      haptic('error');
      setNotice({
        tone: 'destructive',
        text: `Не удалось отправить: ${e instanceof Error ? e.message : ''}`,
      });
    },
  });

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  if (!generation) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Spinner />
        <p className="text-sm text-tg-hint">Загружаем карусель</p>
      </div>
    );
  }

  async function copy(kind: 'text' | 'caption') {
    if (!generation) return;
    const value = kind === 'caption' ? (generation.payload?.caption ?? '') : generation.content;
    try {
      await navigator.clipboard.writeText(value);
      haptic('success');
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // clipboard may be unavailable in WebView
    }
  }

  function handleRewriteSuccess(resp: SessionRewriteResponse) {
    notifyTurboCap(qc, resp.charged_via);
    clearRewriteFeedback(sessionId);
    qc.setQueryData<SessionGetResponse>(['session', sessionId], (old) => {
      if (!old) return old;
      return {
        ...old,
        session: {
          ...old.session,
          status: 'completed',
          free_rewrites_used: resp.free_rewrites_used,
        },
        last_generation: resp.generation,
        rewrite_cost: resp.rewrite_cost,
        render: resp.render,
      };
    });
    qc.invalidateQueries({ queryKey: ['profile'] });
    setRewriteOpen(false);
  }

  const dateStr = new Date(generation.created_at).toLocaleString('ru-RU', {
    dateStyle: 'long',
    timeStyle: 'short',
  });
  const rewriteLabel =
    rewriteCost === 'free'
      ? 'Переписать (бесплатно)'
      : rewriteCost === 'paid'
        ? paidRewriteLabel(pricing.rewrite, turboUntil)
        : 'Переписать (нужны кредиты)';
  const title = generation.payload?.title ?? 'Ваша карусель';

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-8 safe-top safe-bottom">
      {readonly && (
        <Link
          to="/history"
          className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors mb-2"
        >
          <ArrowLeft size={16} />
          Назад
        </Link>
      )}
      <header className="mb-3">
        <h1 className="text-xl font-bold mb-1 leading-tight">{title}</h1>
        <p className="text-xs text-tg-hint">{dateStr}</p>
      </header>

      {rerender.isPending ? (
        <div className="aspect-[4/5] w-full rounded-2xl bg-tg-section-bg flex flex-col items-center justify-center gap-2 mb-3">
          <Spinner />
          <p className="text-sm text-tg-hint">Перерисовываем</p>
        </div>
      ) : (
        <SlideCarousel urls={render?.image_urls ?? []} className="mb-3" />
      )}

      {!render && !rerender.isPending && (
        <Toast tone="warning" className="mb-3">
          {readonly
            ? 'Картинки для этой карусели не сохранились.'
            : 'Картинки не собрались. Выберите стиль ниже, чтобы попробовать ещё раз.'}
        </Toast>
      )}

      {!readonly && stylesQuery.data && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-4 px-4 mb-4">
          {stylesQuery.data.styles.map((s) => {
            const active = s.code === styleCode;
            return (
              <button
                key={s.code}
                type="button"
                disabled={rerender.isPending}
                onClick={() => {
                  if (active) return;
                  haptic('light');
                  rerender.mutate(s.code);
                }}
                aria-pressed={active}
                className={cn(
                  'shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                  active
                    ? 'bg-brand text-brand-on border-brand'
                    : 'bg-tg-section-bg border-transparent text-tg-text/80',
                )}
              >
                {s.name}
              </button>
            );
          })}
        </div>
      )}

      {notice && (
        <Toast tone={notice.tone} className="mb-3">
          {notice.text}
        </Toast>
      )}

      {!readonly && (
        <>
          <Button
            onClick={() => deliver.mutate()}
            disabled={!render || deliver.isPending}
            loading={deliver.isPending}
            variant="primary"
            size="lg"
            fullWidth
            leftIcon={!deliver.isPending ? <Send size={18} /> : undefined}
            className="mb-2"
          >
            Отправить в Telegram
          </Button>
          <Button
            onClick={() => setRewriteOpen(true)}
            disabled={rewriteCost === 'blocked'}
            variant="secondary"
            size="lg"
            fullWidth
            leftIcon={<RotateCcw size={18} />}
            className="mb-2"
          >
            {rewriteLabel}
          </Button>
          {rewriteCost === 'blocked' && (
            <Button
              onClick={() => navigate('/balance')}
              variant="secondary"
              size="lg"
              fullWidth
              leftIcon={<Wallet size={18} />}
              className="mb-2"
            >
              Пополнить баланс
            </Button>
          )}
        </>
      )}

      {generation.payload?.caption && (
        <Card variant="surface" padding="md" className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-tg-hint">
              Описание для публикации
            </h2>
            <button
              type="button"
              onClick={() => copy('caption')}
              className="inline-flex items-center gap-1 text-xs text-brand font-medium"
            >
              {copied === 'caption' ? <Check size={12} /> : <Copy size={12} />}
              {copied === 'caption' ? 'Готово' : 'Копировать'}
            </button>
          </div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {generation.payload.caption}
          </p>
        </Card>
      )}

      <div className="flex gap-2 mb-3">
        <Button
          onClick={() => copy('text')}
          variant="secondary"
          size="md"
          fullWidth
          leftIcon={copied === 'text' ? <Check size={16} /> : <Copy size={16} />}
          hapticKind={false}
        >
          {copied === 'text' ? 'Скопировано' : 'Скопировать текст'}
        </Button>
        <Button
          onClick={() => navigate('/')}
          variant="secondary"
          size="md"
          fullWidth
          leftIcon={<Home size={16} />}
          hapticKind="light"
        >
          На главную
        </Button>
      </div>

      {!readonly && (
        <p className="text-xs text-tg-hint text-center">
          {[
            generation.is_free_rewrite ? 'Бесплатная перегенерация.' : null,
            balance !== null ? `Баланс: ${balance}` : null,
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      )}
      {isAdmin && (
        <p className="text-xs text-tg-hint/70 text-center mt-2 tabular-nums">
          {generation.model ?? '—'} · {generation.tokens_used ?? '—'} t ·{' '}
          {generation.payload?.slides.length ?? 0} слайдов · session #{sessionId}
        </p>
      )}

      {rewriteOpen && !readonly && (
        <RewriteModal
          sessionId={sessionId}
          rewriteCost={rewriteCost}
          initialFeedback={loadRewriteFeedback(sessionId)}
          onSubmitted={handleRewriteSuccess}
          onClose={() => setRewriteOpen(false)}
          onEditAnswers={(fb) => {
            saveRewriteFeedback(sessionId, fb);
            setRewriteOpen(false);
            onEditAnswers();
          }}
        />
      )}
    </div>
  );
}
